import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const TrainingsclubSuccess = () => {
  const { lang } = useLang();
  const t = (de, en) => (lang === "de" ? de : en);
  const [state, setState] = useState("checking"); // checking | active | pending
  const [email, setEmail] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const provider = p.get("provider") || "stripe";
    const sessionId = p.get("session_id");
    const subId = p.get("subscription_id");

    let stop = false;
    const poll = async () => {
      tries.current += 1;
      try {
        let r;
        if (provider === "paypal" && subId) r = await api.get(`/trainingsclub/paypal/confirm/${subId}`);
        else if (sessionId) r = await api.get(`/trainingsclub/status/${sessionId}`);
        else { setState("pending"); return; }
        if (stop) return;
        if (r.data.email) setEmail(r.data.email);
        if (r.data.active) { setState("active"); return; }
      } catch { /* retry */ }
      if (tries.current < 12 && !stop) setTimeout(poll, 2500);
      else if (!stop) setState("pending");
    };
    poll();
    return () => { stop = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-24" data-testid="trainingsclub-success">
      <div className="w-full max-w-md text-center border border-white/10 bg-[#0A0A0A] p-8 sm:p-10 rounded-lg">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin text-[#ff0033] mx-auto mb-5" size={36} />
            <h1 className="font-heading text-2xl uppercase text-white">{t("Wir aktivieren dein Abo …", "Activating your membership …")}</h1>
            <p className="text-neutral-400 text-sm mt-3">{t("Einen Moment bitte, wir bestätigen deine Zahlung.", "One moment, we're confirming your payment.")}</p>
          </>
        )}
        {state === "active" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#ff0033] flex items-center justify-center mx-auto mb-5"><Check size={34} className="text-white" /></div>
            <h1 className="font-heading text-3xl uppercase text-white">{t("Willkommen im Trainingsclub!", "Welcome to the Training Club!")}</h1>
            <p className="text-neutral-300 text-sm mt-3">{t("Deine Mitgliedschaft ist aktiv.", "Your membership is active.")}{email ? ` (${email})` : ""}</p>
            <p className="text-neutral-400 text-sm mt-2">{t("Beim Buchen eines Abo-Kurses setzt du einfach den Haken „Ich bin Trainingsclub-Member“ – die Teilnahme ist dann kostenlos.", "When booking a club course, just tick ‘I'm a Training Club member’ – participation is then free.")}</p>
            <Link to="/kurse" data-testid="tc-to-courses" className="inline-flex items-center gap-2 mt-7 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-3.5 hover:bg-[#cc0029] transition-colors">
              {t("Zu den Kursen", "Browse courses")} <ArrowRight size={18} />
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <AlertCircle className="text-amber-400 mx-auto mb-5" size={36} />
            <h1 className="font-heading text-2xl uppercase text-white">{t("Zahlung wird verarbeitet", "Payment processing")}</h1>
            <p className="text-neutral-400 text-sm mt-3">{t("Deine Zahlung wird noch verarbeitet. Sobald sie bestätigt ist, ist dein Abo aktiv. Du erhältst eine Bestätigung per E-Mail.", "Your payment is still processing. Your membership will activate once confirmed. You'll get an email confirmation.")}</p>
            <Link to="/kurse" data-testid="tc-to-courses" className="inline-flex items-center gap-2 mt-7 border border-white/20 text-white font-mono-lab text-xs uppercase tracking-widest px-8 py-3.5 hover:border-[#ff0033] transition-colors">
              {t("Zu den Kursen", "Browse courses")} <ArrowRight size={16} />
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default TrainingsclubSuccess;
